<div class="partner-page">

  <!-- Hero -->
  <div class="hero-section">
    <h1>Become a Chareety Sales Partner</h1>
    <p class="hero-sub">Help nonprofits thrive. Earn lifetime commissions. Share in the company's success.</p>
  </div>

  <!-- Value Props -->
  <div class="value-section">
    <div class="value-grid">
      <div class="value-card">
        <span class="value-icon material-icons">handshake</span>
        <h3>Own the Relationship</h3>
        <p>You're not just selling — you're the face of Chareety in your community. You onboard, set up, train, and support your organizations.</p>
      </div>
      <div class="value-card">
        <span class="value-icon material-icons">all_inclusive</span>
        <h3>Lifetime Commissions</h3>
        <p>Earn 25% for the first 3 months, then 15% for life — as long as the org stays on Chareety, you earn. Plus $100 per on-site setup visit.</p>
      </div>
      <div class="value-card">
        <span class="value-icon material-icons">public</span>
        <h3>Unlimited Opportunity</h3>
        <p>No territories. No quotas. Over 1.5 million registered nonprofits in the U.S. still use paper and outdated tools. The market is wide open.</p>
      </div>
    </div>
  </div>

  <!-- What You Do -->
  <div class="details-section">
    <h2>What You'll Do</h2>
    <ul class="check-list">
      <li>Identify and connect with local nonprofits who need better donation tools</li>
      <li>Demo the platform and guide organizations through onboarding</li>
      <li>Set up kiosk devices on-site — including wall-mounted installation or coordinating with a local installer</li>
      <li>Be the primary point of contact — training, support, and ongoing success</li>
      <li>Help organizations get the most out of Chareety — donor management, receipts, broadcasts, giving pages</li>
    </ul>
  </div>

  <!-- Earnings -->
  <div class="details-section alt-bg">
    <h2>What You Earn</h2>
    <ul class="check-list">
      <li><strong>25% commission</strong> on all platform revenue from your organizations for the first 3 months</li>
      <li><strong>15% lifetime commission</strong> — as long as they're on Chareety, you earn</li>
      <li><strong>$100 per on-site setup visit</strong> for device installation</li>
      <li><strong>Monthly payouts</strong> via Stripe — no minimum threshold</li>
      <li><strong>Additional incentives</strong> discussed during onboarding call</li>
      <li>Full partner dashboard to track referrals, conversions, and earnings</li>
    </ul>
    <p class="earnings-note">Build a portfolio of organizations and watch your monthly income grow. Every org you add increases both your recurring commissions and your equity stake.</p>
  </div>

  <!-- Who We're Looking For -->
  <div class="details-section">
    <h2>Who We're Looking For</h2>
    <ul class="check-list">
      <li>People embedded in nonprofit, faith-based, or community organizations</li>
      <li>Former or current nonprofit administrators, board members, or consultants</li>
      <li>People passionate about helping organizations focus on their mission instead of paperwork</li>
      <li>Comfortable with technology and able to walk someone through a tablet setup</li>
      <li>Able to install wall-mounted kiosk devices on-site, or coordinate with a local installer</li>
      <li>Self-starters who don't need micromanagement</li>
    </ul>
  </div>

  <!-- Trust Builders -->
  <div class="trust-section">
    <h2>Transparency First</h2>
    <div class="trust-grid">
      <div class="trust-item">
        <span class="trust-icon material-icons">verified</span>
        <h4>Not MLM</h4>
        <p>This is a direct referral model. You earn from organizations you personally refer and support. No recruiting, no downline, no multi-level structure.</p>
      </div>
      <div class="trust-item">
        <span class="trust-icon material-icons">attach_money</span>
        <h4>No Cost to Join</h4>
        <p>There is no fee to become a partner. The only investment is your AML training (~$35 one-time through WebCE).</p>
      </div>
      <div class="trust-item">
        <span class="trust-icon material-icons">description</span>
        <h4>Clear Agreement</h4>
        <p>Every detail is in writing. Review our full <a routerLink="/partner-agreement">Partner Referral Agreement</a> before you commit.</p>
      </div>
      <div class="trust-item">
        <span class="trust-icon material-icons">people</span>
        <h4>Independent Contractor</h4>
        <p>You work on your own terms, your own schedule. This is a partnership, not employment. No quotas, no territories.</p>
      </div>
    </div>
  </div>

  <!-- How It Works -->
  <div class="details-section">
    <h2>How It Works</h2>
    <div class="steps-row">
      <div class="step-item">
        <div class="step-num">1</div>
        <div class="step-text"><strong>Show Interest</strong><br>Fill out the form below</div>
      </div>
      <div class="step-arrow">&rarr;</div>
      <div class="step-item">
        <div class="step-num">2</div>
        <div class="step-text"><strong>We'll Call You</strong><br>Walk through the model together</div>
      </div>
      <div class="step-arrow">&rarr;</div>
      <div class="step-item">
        <div class="step-num">3</div>
        <div class="step-text"><strong>Onboard</strong><br>Complete AML training, get your dashboard</div>
      </div>
      <div class="step-arrow">&rarr;</div>
      <div class="step-item">
        <div class="step-num">4</div>
        <div class="step-text"><strong>Start Earning</strong><br>Refer nonprofits, earn for life</div>
      </div>
    </div>
  </div>

  <!-- Interest Form -->
  <div class="form-section" *ngIf="!submitted">
    <h2>Interested? Let's Talk.</h2>
    <p class="form-intro">Fill out the form below and we'll schedule a call to walk you through the opportunity. No commitment — just a conversation.</p>
    <form (ngSubmit)="submitForm()" #partnerForm="ngForm" class="partner-form">
      <div class="form-row">
        <div class="form-group">
          <label for="name">Full Name *</label>
          <input type="text" id="name" name="name" [(ngModel)]="formData.name" required placeholder="Your full name">
        </div>
        <div class="form-group">
          <label for="email">Email Address *</label>
          <input type="email" id="email" name="email" [(ngModel)]="formData.email" required email placeholder="your&#64;email.com">
        </div>
      </div>
      <div class="form-row">
        <div class="form-group">
          <label for="phone">Phone Number *</label>
          <input type="tel" id="phone" name="phone" [(ngModel)]="formData.phone" required placeholder="(555) 123-4567">
        </div>
        <div class="form-group">
          <label for="location">City / State</label>
          <input type="text" id="location" name="location" [(ngModel)]="formData.location" placeholder="Chicago, IL">
        </div>
      </div>
      <div class="form-group">
        <label for="background">Your Background</label>
        <input type="text" id="background" name="background" [(ngModel)]="formData.background" placeholder="e.g., Nonprofit board member, community organizer, sales professional">
      </div>
      <div class="form-group">
        <label for="whyInterested">Why are you interested in becoming a Chareety partner? *</label>
        <textarea id="whyInterested" name="whyInterested" rows="4" [(ngModel)]="formData.whyInterested" required placeholder="Tell us about your connection to the nonprofit community and what excites you about this opportunity..."></textarea>
      </div>
      <button mat-raised-button color="primary" type="submit" [disabled]="!partnerForm.form.valid">Submit Interest</button>
    </form>
  </div>

  <!-- Success Message -->
  <div class="success-section" *ngIf="submitted">
    <span class="success-icon material-icons">check_circle</span>
    <h2>Thank You!</h2>
    <p>We've received your interest. A member of our team will reach out within 48 hours to schedule a call and walk you through the Chareety partner opportunity.</p>
    <p class="success-email">Questions? Email us at <a href="mailto:admin&#64;chareety.com">admin&#64;chareety.com</a></p>
  </div>

</div>
